import { useState, useEffect, useCallback, useRef } from 'react';
import trainingApi from '../../services/trainingApi';

/**
 * Custom hook for managing a training session lifecycle.
 * Handles messages, typing indicators, session state, and socket events.
 */
function useTrainingSession(sessionId, socket) {
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState(null);
  const [scenarioBrief, setScenarioBrief] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | active | completing | completed | timed_out | error
  const [error, setError] = useState(null);
  const [isPatientTyping, setIsPatientTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(null);
  const [timerWarning, setTimerWarning] = useState(null);
  const [totalTurns, setTotalTurns] = useState(0);
  const [assessmentsAdministered, setAssessmentsAdministered] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseInFlight, setPauseInFlight] = useState(false);

  const timerRef = useRef(null);
  const processedTurns = useRef(new Set());

  // Load existing session
  useEffect(() => {
    if (!sessionId || sessionId === 'new') return;

    const loadSession = async () => {
      try {
        setStatus('loading');
        const response = await trainingApi.getSession(sessionId);
        const _raw_data = response.data || response; const data = _raw_data.data || _raw_data;
        const sessionData = data.session || data;

        setSession(sessionData);
        setScenarioBrief(data.scenarioBrief || null);

        if (sessionData.transcript) {
          setMessages(sessionData.transcript);
          sessionData.transcript.forEach(m => processedTurns.current.add(m.turnIndex));
          setTotalTurns(sessionData.transcript.length);
        }

        if (sessionData.sessionState) {
          if (sessionData.sessionState.timeRemainingSeconds != null) {
            setTimeRemainingSeconds(sessionData.sessionState.timeRemainingSeconds);
          }
          if (sessionData.sessionState.assessmentsAdministered) {
            setAssessmentsAdministered(
              Array.isArray(sessionData.sessionState.assessmentsAdministered)
                ? sessionData.sessionState.assessmentsAdministered
                : []
            );
          }
        }

        if (sessionData.timeLimitSeconds && sessionData.status === 'in_progress') {
          setTimeRemainingSeconds(prev => prev != null ? prev : sessionData.timeLimitSeconds);
        }

        // Pause state — therapist_practice sessions can be paused mid-flight.
        if (sessionData.pausedAt) {
          setIsPaused(true);
        }

        setStatus(sessionData.status === 'in_progress' ? 'active' : sessionData.status || 'completed');
      } catch (err) {
        console.error('[useTrainingSession] Failed to load session:', err);
        setError(err.message || 'Failed to load session');
        setStatus('error');
      }
    };

    loadSession();
  }, [sessionId]);

  // Client-side timer countdown — paused state freezes the clock so a
  // therapist who steps away doesn't burn through their advisory window.
  useEffect(() => {
    if (status !== 'active' || timeRemainingSeconds == null || isPaused) return;

    timerRef.current = setInterval(() => {
      setTimeRemainingSeconds(prev => {
        if (prev == null || prev <= 0) {
          clearInterval(timerRef.current);
          return 0;
        }
        const next = prev - 1;
        if (next === 300) setTimerWarning('5min');
        else if (next === 60) setTimerWarning('1min');
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status, timeRemainingSeconds != null, isPaused]);

  // Socket event listeners
  useEffect(() => {
    if (!socket || !sessionId) return;

    const handlePatientResponse = (data) => {
      const msg = data.patientResponse || data;
      if (processedTurns.current.has(msg.turnIndex)) return;
      // Mark as processed immediately (synchronously) to prevent HTTP response race
      processedTurns.current.add(msg.turnIndex);
      setMessages(prev => {
        const isDupe = prev.some(m => m.actor === msg.actor && m.content === msg.content);
        if (isDupe) return prev;
        return [...prev, msg];
      });
      setIsPatientTyping(false);
      setTotalTurns(prev => prev + 1);

      // Sync timer from server
      if (data.sessionState?.timeRemainingSeconds != null) {
        setTimeRemainingSeconds(data.sessionState.timeRemainingSeconds);
      }
    };

    const handleTypingIndicator = (data) => {
      setIsPatientTyping(data.isTyping !== false);
    };

    const handleTimerWarning = (data) => {
      setTimerWarning(data.warning || '5min');
    };

    const handleTimerExpired = () => {
      // Advisory only for MVP — warn the student but don't block the session
      setTimerWarning('expired');
      setTimeRemainingSeconds(0);
      if (timerRef.current) clearInterval(timerRef.current);
    };

    const handleSessionComplete = (data) => {
      setStatus('completed');
      if (data.evaluation) setEvaluation(data.evaluation);
      if (timerRef.current) clearInterval(timerRef.current);
    };

    const handleEvaluationComplete = (data) => {
      setEvaluation(data.evaluation || data);
    };

    // Voice mode: student transcript persisted by server, add to messages
    const handleStudentResponse = (data) => {
      const msg = data;
      if (processedTurns.current.has(msg.turnIndex)) return;
      // Mark as processed immediately (synchronously) to prevent HTTP response race
      processedTurns.current.add(msg.turnIndex);
      setMessages(prev => {
        const isDupe = prev.some(m => m.actor === msg.actor && m.content === msg.content);
        if (isDupe) return prev;
        return [...prev, msg];
      });
      setTotalTurns(prev => prev + 1);
    };

    // A goal was suggested (text or voice). The server emits this for both, so
    // the goal line appears in the chat whichever way it was sent.
    const handleGoalProposed = (data) => {
      if (data.turnIndex == null || processedTurns.current.has(data.turnIndex)) return;
      processedTurns.current.add(data.turnIndex);
      setMessages(prev => [...prev, {
        turnIndex: data.turnIndex,
        actor: 'system',
        content: data.content,
        timestamp: data.timestamp || new Date().toISOString(),
        metadata: data.metadata || { isGoalProposal: true },
      }]);
      setTotalTurns(prev => prev + 1);
    };

    socket.on('training:patient_response', handlePatientResponse);
    socket.on('training:student_response', handleStudentResponse);
    socket.on('training:goal_proposed', handleGoalProposed);
    socket.on('training:typing_indicator', handleTypingIndicator);
    socket.on('training:timer_warning', handleTimerWarning);
    socket.on('training:timer_expired', handleTimerExpired);
    socket.on('training:session_complete', handleSessionComplete);
    socket.on('training:evaluation_complete', handleEvaluationComplete);

    return () => {
      socket.off('training:patient_response', handlePatientResponse);
      socket.off('training:student_response', handleStudentResponse);
      socket.off('training:goal_proposed', handleGoalProposed);
      socket.off('training:typing_indicator', handleTypingIndicator);
      socket.off('training:timer_warning', handleTimerWarning);
      socket.off('training:timer_expired', handleTimerExpired);
      socket.off('training:session_complete', handleSessionComplete);
      socket.off('training:evaluation_complete', handleEvaluationComplete);
    };
  }, [socket, sessionId]);

  // Send message with optimistic UI
  const sendMessage = useCallback(async (content) => {
    if (!sessionId || isSending || status !== 'active') return;

    const studentMsg = {
      turnIndex: totalTurns,
      actor: 'student',
      content,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, studentMsg]);
    processedTurns.current.add(studentMsg.turnIndex);
    setTotalTurns(prev => prev + 1);
    setIsSending(true);
    setSendError(null);
    setIsPatientTyping(true);

    try {
      const response = await trainingApi.sendMessage(sessionId, content);
      const _raw_data = response.data || response; const data = _raw_data.data || _raw_data;

      // Add patient response if not already received via socket
      if (data.patientResponse && !processedTurns.current.has(data.patientResponse.turnIndex)) {
        processedTurns.current.add(data.patientResponse.turnIndex);
        setMessages(prev => {
          const isDupe = prev.some(m => m.actor === data.patientResponse.actor && m.content === data.patientResponse.content);
          if (isDupe) return prev;
          return [...prev, data.patientResponse];
        });
        setTotalTurns(prev => prev + 1);
      }

      // Sync session state
      if (data.sessionState) {
        if (data.sessionState.timeRemainingSeconds != null) {
          setTimeRemainingSeconds(data.sessionState.timeRemainingSeconds);
        }
        if (data.sessionState.totalTurns != null) {
          setTotalTurns(data.sessionState.totalTurns);
        }
      }

      setIsPatientTyping(false);
    } catch (err) {
      console.error('[useTrainingSession] Send message error:', err);
      setSendError(err.message || 'Failed to send message');
      setIsPatientTyping(false);
      // Remove optimistic student message on error
      setMessages(prev => prev.filter(m => m !== studentMsg));
      processedTurns.current.delete(studentMsg.turnIndex);
      setTotalTurns(prev => prev - 1);
    } finally {
      setIsSending(false);
    }
  }, [sessionId, isSending, status, totalTurns]);

  // Suggest a goal in a TEXT session. The patient's reply is generated
  // server-side and arrives here in the response (and over the socket; both
  // paths are de-duplicated by turn index). Voice calls do not use this: the
  // page sends those over the call's socket.
  const proposeGoal = useCallback(async ({ description, goalType }) => {
    if (!sessionId || status !== 'active') return null;
    setIsPatientTyping(true);
    try {
      const response = await trainingApi.proposeGoal(sessionId, { description, goalType });
      const _raw_data = response.data || response; const data = _raw_data.data || _raw_data;

      const addOnce = (msg) => {
        if (!msg || processedTurns.current.has(msg.turnIndex)) return;
        processedTurns.current.add(msg.turnIndex);
        setMessages(prev => [...prev, msg]);
        setTotalTurns(prev => prev + 1);
      };
      addOnce(data.goal);
      addOnce(data.patientResponse);
      return data;
    } finally {
      setIsPatientTyping(false);
    }
  }, [sessionId, status]);

  // Request assessment
  const requestAssessment = useCallback(async (testKey) => {
    if (!sessionId) return;
    try {
      const response = await trainingApi.requestAssessment(sessionId, testKey);
      const _raw_data = response.data || response; const data = _raw_data.data || _raw_data;
      setAssessmentsAdministered(prev => [...prev, testKey]);

      // Add system message for assessment result
      // Backend returns { assessment: { testKey, testName, score, maxScore, severityLevel, interpretation, ... } }
      const assessmentData = data.assessment || data.assessmentResult || data;
      console.log('[useTrainingSession] Assessment response data:', JSON.stringify(data).substring(0, 500));

      if (assessmentData && (assessmentData.score !== undefined || assessmentData.testKey)) {
        const result = assessmentData;
        const name = result.testName || testKey.toUpperCase();
        const severity = result.severityLevel || result.severity || '';
        const interpretation = result.interpretation || '';
        const score = result.score ?? 'N/A';
        const maxScore = result.maxScore ? `/${result.maxScore}` : '';
        const criticalFlag = result.hasCriticalAlerts ? ' [CRITICAL ALERT]' : '';

        const systemMsg = {
          turnIndex: totalTurns,
          actor: 'system',
          content: `${name} Assessment Complete — Score: ${score}${maxScore} (${severity})${criticalFlag}\n${interpretation}`,
          timestamp: new Date().toISOString(),
          metadata: {
            isAssessmentResult: true,
            testKey: result.testKey || testKey,
            testName: name,
            score,
            maxScore: result.maxScore,
            severityLevel: severity,
            interpretation,
            hasCriticalAlerts: result.hasCriticalAlerts || false,
            answers: result.answers || [],
          },
        };
        setMessages(prev => [...prev, systemMsg]);
        setTotalTurns(prev => prev + 1);
      } else {
        // Fallback: add a simple notification even if rich data wasn't parsed
        setMessages(prev => [...prev, {
          turnIndex: totalTurns,
          actor: 'system',
          content: `Assessment ${testKey} administered. Results recorded.`,
          timestamp: new Date().toISOString(),
          metadata: { isAssessmentResult: true, testKey },
        }]);
        setTotalTurns(prev => prev + 1);
      }

      return data;
    } catch (err) {
      console.error('[useTrainingSession] Request assessment error:', err);
      throw err;
    }
  }, [sessionId, totalTurns]);

  // Complete session
  const completeSession = useCallback(async (reason) => {
    if (!sessionId) return;
    try {
      setStatus('completing');
      const response = await trainingApi.completeSession(sessionId, reason);
      const _raw_data = response.data || response; const data = _raw_data.data || _raw_data;
      setStatus('completed');
      if (timerRef.current) clearInterval(timerRef.current);
      return data;
    } catch (err) {
      console.error('[useTrainingSession] Complete session error:', err);
      setStatus('active');
      throw err;
    }
  }, [sessionId]);

  const clearSendError = useCallback(() => setSendError(null), []);

  // Pause / resume — only meaningful for therapist_practice sessions.
  const pauseSession = useCallback(async () => {
    if (!sessionId || pauseInFlight || isPaused) return;
    setPauseInFlight(true);
    try {
      const response = await trainingApi.pauseSession(sessionId);
      const data = response?.data?.data || response?.data || response;
      if (data?.timeRemainingSeconds != null) setTimeRemainingSeconds(data.timeRemainingSeconds);
      setIsPaused(true);
    } catch (err) {
      console.error('[useTrainingSession] Pause error:', err);
      throw err;
    } finally {
      setPauseInFlight(false);
    }
  }, [sessionId, isPaused, pauseInFlight]);

  const resumeSession = useCallback(async () => {
    if (!sessionId || pauseInFlight || !isPaused) return;
    setPauseInFlight(true);
    try {
      const response = await trainingApi.resumeSession(sessionId);
      const data = response?.data?.data || response?.data || response;
      if (data?.timeRemainingSeconds != null) setTimeRemainingSeconds(data.timeRemainingSeconds);
      setIsPaused(false);
    } catch (err) {
      console.error('[useTrainingSession] Resume error:', err);
      throw err;
    } finally {
      setPauseInFlight(false);
    }
  }, [sessionId, isPaused, pauseInFlight]);

  return {
    messages,
    session,
    scenarioBrief,
    evaluation,
    status,
    error,
    isPatientTyping,
    isSending,
    sendError,
    timeRemainingSeconds,
    timerWarning,
    totalTurns,
    assessmentsAdministered,
    isPaused,
    pauseInFlight,
    sendMessage,
    requestAssessment,
    proposeGoal,
    completeSession,
    pauseSession,
    resumeSession,
    clearSendError,
  };
}

export default useTrainingSession;
